// hooks/use-offers.ts
import { useState } from "react";
import { toast } from "react-toastify";
import { useToggleReferralStatus } from '../../../apis/offers/toggle-offer-status';
import { useDeleteReferralOffer } from '../../../apis/referral-offers/delete-referral-offer';
import { useGetAllReferralOffers } from '../../../apis/referral-offers/get-all-referral-offers';
import { docsOffers } from '../../../utils/types';
import { formatDate } from '../../../utils/helpers';

const useOffers = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [validFrom, setValidFrom] = useState("");
    const [validTo, setValidTo] = useState("");
    const [isActive, setIsActive] = useState("");
    const [userType, setUserType] = useState("");
    const [filters, setFilters] = useState({ validFrom: "", validTo: "", isActive: "", userType: "" });
    const [selectedOffer, setSelectedOffer] = useState<docsOffers | null>(null);
    const [isAddOfferModalVisible, setIsAddOfferModalVisible] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [offerStatusChangeId, setOfferStatusChangeId] = useState("");

    const { data, isFetching: isLoading, refetch } = useGetAllReferralOffers({
        page: currentPage,
        userType: filters.userType,
        isActive: filters.isActive,
        startDate: filters.validFrom,
        expiryDate: filters.validTo,
    });

    const { mutateAsync: deleteOffer, isPending: isDeletingOffer } = useDeleteReferralOffer();
    const { mutateAsync: toggleStatus } = useToggleReferralStatus();

    const offers = data?.docs ?? [];
    const totalPages = data?.totalPages ?? 1;

    const handleSearch = () => {
        setCurrentPage(1);
        setFilters({ validFrom, validTo, userType, isActive });
    };

    const clearFilters = () => {
        setCurrentPage(1);
        setValidFrom("");
        setValidTo("");
        setUserType("");
        setIsActive("");
        setFilters({ validFrom: "", validTo: "", userType: "", isActive: "" });
    };

    const handleDeleteOffer = async () => {
        try {
            await deleteOffer(selectedOffer?._id || "");
            toast.success("Offer Deleted Successfully");
            if (offers.length <= 1 && currentPage > 1) {
                setCurrentPage((prev) => prev - 1);
            } else {
                refetch();
            }
            setIsDeleteModalOpen(false);
            setSelectedOffer(null);
        } catch (error: any) {
            toast.error(error?.response?.data?.error || "Something went wrong");
        }
    };

    const handleOfferStatusChange = async (id: string, isOfferActive: boolean) => {
        setOfferStatusChangeId(id);
        try {
            await toggleStatus({ id, isOfferActive: !isOfferActive });
            refetch();
        } catch {
            toast.error("Something went wrong");
        }
        setOfferStatusChangeId("");
    };

    const handleFromDateChange = (e: Date | null | undefined) =>
        setValidFrom(formatDate(e?.toString() || ""));

    const handleToDateChange = (e: Date | null | undefined) =>
        setValidTo(formatDate(e?.toString() || ""));

    const isAnyFilterApplied = !!userType || !!isActive || !!validFrom || !!validTo;

    const formatsDate = (inputDate: string) =>
        new Date(inputDate)
            .toLocaleString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            })
            .replace(/\//g, "-");

    const formatUserType = (str: string) => {
        if (str === "individual") return "Individual user";
        if (str === "business") return "Business user";
        return str.charAt(0).toUpperCase() + str.slice(1);
    };



    return {
        offers,
        totalPages,
        isLoading,
        isDeletingOffer,
        currentPage,
        setCurrentPage,
        validFrom,
        validTo,
        isActive,
        setIsActive,
        userType,
        setUserType,
        selectedOffer,
        setSelectedOffer,
        isAddOfferModalVisible,
        setIsAddOfferModalVisible,
        isEditModalOpen,
        setIsEditModalOpen,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        offerStatusChangeId,
        isAnyFilterApplied,
        handleSearch,
        clearFilters,
        handleDeleteOffer,
        handleOfferStatusChange,
        handleFromDateChange,
        handleToDateChange,
        formatsDate,
        formatUserType,
        refetch,
    };
};

export default useOffers;